/**
 * スポット価格画面 — エリア定義・画面の行の型・日付 helper。
 *
 * 約定価格は BFF `BiddingSpotMarketService` から取得する (`pages/SpotPrice/hooks/
 * useListSpotMarketPrices.ts`)。 ここに残る天気 (`WEATHER_MOCK`) だけが mock の値で、
 * 実データ源は別途 (#1682 の約定価格とは別の残論点)。 日本語の area name は API 由来
 * データ扱いのため i18n 対象外。
 */
import { LightModeSemanticColors } from '@enechain/ecloud-designsystem'

import { SPOT_PRICE_CHART_5_COLOR } from '~/constants/chartPalette'
import { now as serverNow } from '~/models/clock'
import { jstWallClockToInstant, startOfTodayJst } from '~/models/dateTime'

/** エリア定義 (固定 10 系列: system + 9 エリア) */
export type AreaDef = {
  code: PriceSeriesCode
  name: string
  /** Recharts stroke / checkbox fill color (hex or rgba). */
  color: string
  /** true = システムプライス (master「全エリア」対象外・チャート破線・コマ別表の着色対象外) */
  isSystem?: boolean
}

// chart-5 (#CC79A7 赤紫) は LightModeSemanticColors に対応 key が無いため
// chartPalette.ts に集約 (D-1)。
const CHART_5_COLOR = SPOT_PRICE_CHART_5_COLOR

export const AREAS: AreaDef[] = [
  {
    code: 'system',
    name: 'システムプライス',
    color: LightModeSemanticColors.Text.midEmphasis.value, // rgba(0,0,0,0.62) ≒ neutral grey
    isSystem: true,
  },
  {
    code: 'hokkaido',
    name: '北海道',
    color: LightModeSemanticColors.Brand.purple.value, // #bb97ff
  },
  {
    code: 'tohoku',
    name: '東北',
    color: LightModeSemanticColors.Brand.blue.value, // #0c00c5
  },
  {
    code: 'tokyo',
    name: '東京',
    color: LightModeSemanticColors.Brand.red.value, // #ff1e23
  },
  {
    code: 'chubu',
    name: '中部',
    color: LightModeSemanticColors.Brand.yellow.value, // #ffdd29
  },
  {
    code: 'hokuriku',
    name: '北陸',
    color: LightModeSemanticColors.Brand.green.value, // #69d582
  },
  {
    code: 'kansai',
    name: '関西',
    color: LightModeSemanticColors.Brand.cyan.value, // #597eff
  },
  {
    code: 'chugoku',
    name: '中国',
    color: CHART_5_COLOR,
  },
  {
    code: 'shikoku',
    name: '四国',
    color: LightModeSemanticColors.Brand.pink.value, // #ff6e6e
  },
  {
    code: 'kyushu',
    name: '九州',
    color: LightModeSemanticColors.Brand.teal.value, // #00b0b2
  },
]

/** 価格を持つ系列のコード (system + 9 エリア。 `AREAS` の code と同じ)。 */
export type PriceSeriesCode =
  | 'system'
  | 'hokkaido'
  | 'tohoku'
  | 'tokyo'
  | 'chubu'
  | 'hokuriku'
  | 'kansai'
  | 'chugoku'
  | 'shikoku'
  | 'kyushu'

/**
 * 1 受渡日 × 1 コマの価格行 (BFF `BiddingSpotMarketKomaPrices` を画面向けにした形)。
 *
 * eSLJ が価格を返していない系列は `null` (0 にしない。 チャートは線を切り、 表は「—」、
 * 統計は計算から除く)。
 */
export type KomaRow = Record<PriceSeriesCode, number | null> & {
  /** 受渡日 `YYYY-MM-DD`。 */
  date: string
  koma: number
  /** 'HH:MM' 開始時刻。 */
  time: string
  /** 'HH:MM～HH:MM'。 */
  timeRange: string
  /** チャートの X 軸キー `YYYY-MM-DD HH:MM` (複数日を連結しても一意)。 */
  x: string
}

/** 行の 1 系列の価格 (`code` は `AREAS` の code)。 値が無ければ null。 */
export function komaPrice(row: KomaRow, code: PriceSeriesCode): number | null {
  return row[code]
}

/**
 * コマ別エリア価格の価格グループ色 (design `bidding/spot-price.html` の
 * `PRICE_GROUP_COLORS`)。システムプライスと異なる価格を昇順に並べ、この順で割り当てる。
 * index 4 が index 0 と同色なのも design のまま (5 グループ以上に割れるコマはほぼ無い)。
 */
export const PRICE_GROUP_COLORS: string[] = [
  LightModeSemanticColors.Surface.accentPrimary.value,
  LightModeSemanticColors.Brand.red.value,
  LightModeSemanticColors.Text.positiveMid.value,
  LightModeSemanticColors.Text.warningHigh.value,
  LightModeSemanticColors.Surface.accentPrimary.value,
  LightModeSemanticColors.Brand.cyan.value,
  LightModeSemanticColors.Brand.pink.value,
  LightModeSemanticColors.Brand.green.value,
  LightModeSemanticColors.Brand.yellow.value,
  LightModeSemanticColors.Brand.purple.value,
]

/**
 * YYYY-MM-DD 文字列をローカル日付として parse する (= `new Date(str)` は UTC 解釈で
 * JST と 9hr ずれるため。 memory feedback_date_local_parse_pitfall)
 */
export function parseLocalYmd(s: string): Date | null {
  const parts = s.split('-')
  if (parts.length !== 3) return null
  const d = new Date(
    parseInt(parts[0], 10),
    parseInt(parts[1], 10) - 1,
    parseInt(parts[2], 10),
  )
  return isNaN(d.getTime()) ? null : d
}

export function fmtYmd(d: Date): string {
  return (
    d.getFullYear() +
    '-' +
    String(d.getMonth() + 1).padStart(2, '0') +
    '-' +
    String(d.getDate()).padStart(2, '0')
  )
}

/**
 * JEPX スポットは受渡日 D の約定を D-1 の 10:00 に確定・公開するため、
 * 10:00 以降は翌日 / 以前は当日 を max 選択可能日とする。
 *
 * **暦日も 10:00 境界も JST 壁時計で判定する**。 移行前は `now.getFullYear()` 系と
 * `now.getHours() >= 10` を使っており、 非 JST 端末では
 * (a) 起点の暦日が JST と最大 1 日ズレ (b) **JEPX の 10:00 ゲートが端末時刻で
 * 発火する** という 2 つのズレがあった。 (b) は制度上の時刻なので端末に依存して
 * はならない。
 *
 * 境界判定は「JST 10:00 の絶対時刻」を作って絶対時刻どうしで比べる
 * (= `jstWallClockToInstant` の用途そのもの)。 `now` は test 容易性のため注入可能。
 */
export function getMaxSelectableDate(now: Date = serverNow()): Date {
  const todayJst = startOfTodayJst(now)
  const gate = jstWallClockToInstant(todayJst, 10, 0)
  const base = new Date(todayJst)
  if (now.getTime() >= gate.getTime()) base.setDate(base.getDate() + 1)
  return base
}

export const DOW_JA = ['日', '月', '火', '水', '木', '金', '土']

// 集約統計で使う天気 mock (API 由来扱いのため i18n 対象外)
export const WEATHER_MOCK: Record<string, string> = {
  system: '☀️ 晴',
  hokkaido: '🌧️ 雨',
  tohoku: '⛅ 曇',
  tokyo: '☀️ 晴',
  chubu: '☀️ 晴',
  hokuriku: '⛅ 曇',
  kansai: '☀️ 晴',
  chugoku: '🌧️ 雨',
  shikoku: '⛅ 曇',
  kyushu: '☀️ 晴',
}
